import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Bell, BellRing, CheckCheck, Wrench, ClipboardList, LifeBuoy, X, FileText, Receipt, Inbox, ScanText, AlertCircle, Sparkles, MessageSquareText, Brain, Truck, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useUserNotifications } from "../lib/userNotifications";
import { enableWebPush, pushSupported } from "../lib/webpush";

const KIND_META = {
  task_assigned: { Icon: ClipboardList, color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  jobcard_assigned: { Icon: Wrench, color: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  ticket_assigned: { Icon: LifeBuoy, color: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/30" },
  sales_doc_quote: { Icon: FileText, color: "text-cyan-300", bg: "bg-cyan-500/10", border: "border-cyan-500/30" },
  sales_doc_sales_order: { Icon: FileText, color: "text-cyan-300", bg: "bg-cyan-500/10", border: "border-cyan-500/30" },
  sales_doc_invoice: { Icon: Receipt, color: "text-violet-300", bg: "bg-violet-500/10", border: "border-violet-500/30" },
  sales_doc_statement: { Icon: Receipt, color: "text-violet-300", bg: "bg-violet-500/10", border: "border-violet-500/30" },
  sales_doc_credit_note: { Icon: Inbox, color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  lead_followup: { Icon: ClipboardList, color: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  // Portal → CRM (staff receive)
  portal_order_received: { Icon: Inbox, color: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/30" },
  order_received: { Icon: Inbox, color: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/30" },
  portal_ticket_created: { Icon: LifeBuoy, color: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/30" },
  // CRM → Portal (client receives)
  portal_doc_added: { Icon: FileText, color: "text-cyan-300", bg: "bg-cyan-500/10", border: "border-cyan-500/30" },
  portal_equipment_added: { Icon: Wrench, color: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  portal_jobcard_created: { Icon: Wrench, color: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  portal_ticket_opened: { Icon: LifeBuoy, color: "text-violet-300", bg: "bg-violet-500/10", border: "border-violet-500/30" },
  ocr_ready: { Icon: ScanText, color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  ocr_failed: { Icon: AlertCircle, color: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/30" },
  daily_brief: { Icon: Sparkles, color: "text-[#1FB6C1]", bg: "bg-[#1FB6C1]/10", border: "border-[#1FB6C1]/30" },
  // Wave 19 #103 — Trip Deep Itinerary + post-trip Debrief
  trip_deep_itinerary_ready:  { Icon: Brain,             color: "text-[#C4B5FD]", bg: "bg-[#A78BFA]/10", border: "border-[#A78BFA]/30" },
  trip_deep_itinerary_failed: { Icon: AlertCircle,       color: "text-rose-300",  bg: "bg-rose-500/10",   border: "border-rose-500/30" },
  trip_debrief_pending:       { Icon: MessageSquareText, color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  // Wave 19 #108 — Hidro Coach
  coach_checkin_due:          { Icon: Brain,             color: "text-[#C4B5FD]", bg: "bg-[#A78BFA]/10", border: "border-[#A78BFA]/30" },
  // Iter 400.11 — Picking slip unlock authorization
  picking_unlock_request:     { Icon: AlertCircle,       color: "text-amber-300",  bg: "bg-amber-500/10",  border: "border-amber-500/30" },
  // Iter 400.15 — Portal-requested performance-reading edit awaiting approval
  perf_edit_request:          { Icon: AlertCircle,       color: "text-amber-300",  bg: "bg-amber-500/10",  border: "border-amber-500/30" },
};

function fmtRelative(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = (Date.now() - t) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationsBell({ enabled = true }) {
  const navigate = useNavigate();
  const { items, unread, markRead, markAllRead, permission, requestPermission, procurementOverdue } =
    useUserNotifications({ enabled });
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, right: 0 });
  const popRef = useRef(null);
  const btnRef = useRef(null);

  // Recompute the panel position whenever it opens or the window resizes —
  // we render the panel via a React portal at <body> so ancestor
  // `overflow:hidden` headers (CustomerPortalView) can't clip it.
  useEffect(() => {
    if (!open) return undefined;
    const compute = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      setCoords({
        top: Math.round(r.bottom + 8),
        right: Math.round(window.innerWidth - r.right),
      });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open]);

  // Close on outside click — has to consider both the button and the
  // portal-mounted panel.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      const inBtn = btnRef.current?.contains(e.target);
      const inPanel = popRef.current?.contains(e.target);
      if (!inBtn && !inPanel) setOpen(false);
    };
    window.addEventListener("mousedown", onDocClick);
    return () => window.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Auto-subscribe on mount if permission already granted (re-binds when the SW endpoint rotates).
  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof Notification === "undefined") return undefined;
    if (Notification.permission !== "granted" || !pushSupported()) return undefined;
    enableWebPush().catch(() => {});

    // Listen for SW messages so notification clicks navigate cleanly.
    const onMsg = (event) => {
      const t = event?.data?.type;
      if (t === "HV_NAVIGATE" && event.data.url) navigate(event.data.url);
      else if (t === "HV_RESUBSCRIBE") enableWebPush().catch(() => {});
    };
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", onMsg);
    }
    return () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", onMsg);
      }
    };
  }, [enabled, navigate]);

  if (!enabled) return null;

  const onItemClick = async (n) => {
    if (!n.is_read) {
      markRead(n.id);
    }
    setOpen(false);
    if (n.kind === "daily_brief") {
      navigate(`/inbox?focus=${n.id}`);
      return;
    }
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          if (permission === "default") requestPermission();
        }}
        className="hv-btn hv-btn-ghost px-2 py-1.5 relative"
        title="Notifications"
        data-testid="notifications-bell"
      >
        {unread > 0 || procurementOverdue.count > 0 ? (
          <BellRing className="w-4 h-4 text-rose-300 hv-flicker" />
        ) : (
          <Bell className="w-4 h-4" />
        )}
        {(unread > 0 || procurementOverdue.count > 0) && (
          <span
            className="absolute -top-1 -right-1 bg-rose-500 text-white text-[9px] font-bold rounded-full px-1 py-0 min-w-[16px] h-4 flex items-center justify-center"
            data-testid="notifications-bell-badge"
          >
            {(() => {
              const total = unread + procurementOverdue.count;
              return total > 99 ? "99+" : total;
            })()}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={popRef}
          className="fixed w-80 max-w-[calc(100vw-1rem)] hv-card p-0 shadow-2xl border-[#1FB6C1]/40 z-[1000]"
          style={{ top: coords.top, right: coords.right }}
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
            <div className="text-sm font-bold">
              Notifications
              {unread > 0 && (
                <span className="ml-2 text-[10px] font-mono text-rose-300">
                  {unread} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="hv-btn hv-btn-ghost text-[10px] px-1.5 py-0.5"
                  title="Mark all read"
                  data-testid="notifications-mark-all-read"
                >
                  <CheckCheck className="w-3 h-3" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="hv-btn hv-btn-ghost px-1.5 py-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {procurementOverdue.count > 0 && (
              <button
                type="button"
                onClick={() => { setOpen(false); navigate("/procurement"); }}
                className="w-full text-left flex gap-2 px-3 py-2.5 border-b border-white/5 hover:bg-white/[0.03] bg-amber-500/5 transition"
                data-testid="notifications-procurement-tile"
              >
                <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 bg-amber-500/15 border border-amber-500/40 text-amber-300">
                  <Truck className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-amber-200 truncate">
                    {procurementOverdue.count} procurement item
                    {procurementOverdue.count === 1 ? "" : "s"} overdue
                  </div>
                  <div className="text-[10px] text-[#a0aabf]">
                    Worst is {procurementOverdue.worst_days}d late · click to review
                  </div>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-[#a0aabf] shrink-0 mt-1" />
              </button>
            )}
            {items.length === 0 ? (
              procurementOverdue.count > 0 ? null : (
                <div className="text-xs text-[#647087] italic text-center py-6 px-3">
                  You&apos;re all caught up.
                </div>
              )
            ) : (
              <ul className="divide-y divide-white/5">
                {items.map((n) => {
                  const meta = KIND_META[n.kind] || {
                    Icon: Bell,
                    color: "text-[#1fb6c1]",
                    bg: "bg-[#1fb6c1]/10",
                    border: "border-[#1fb6c1]/30",
                  };
                  const { Icon } = meta;
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => onItemClick(n)}
                        className={`w-full text-left flex gap-2 px-3 py-2.5 transition hover:bg-white/[0.03] ${
                          n.is_read ? "" : "bg-[#1fb6c1]/[0.05]"
                        }`}
                        data-testid={`notification-${n.id}`}
                      >
                        <div
                          className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${meta.bg} ${meta.border} border ${meta.color}`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-xs font-medium leading-snug truncate">
                              {n.title}
                            </div>
                            {!n.is_read && (
                              <span
                                className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1 shrink-0"
                                aria-label="Unread"
                              />
                            )}
                          </div>
                          {n.body && (
                            <div className="text-[10px] text-[#a0aabf] truncate">
                              {n.body}
                            </div>
                          )}
                          <div className="text-[9px] text-[#647087] mt-0.5 flex items-center gap-1">
                            <span>{fmtRelative(n.created_at)}</span>
                            {n.by_user_name && <span>· by {n.by_user_name}</span>}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {permission !== "granted" && (
            <div className="px-3 py-2 border-t border-white/10 text-[10px] text-[#a0aabf] flex items-center justify-between gap-2">
              <span>Get notifications even when the app is closed?</span>
              <button
                type="button"
                onClick={async () => {
                  // Request permission via the bell's existing helper
                  const p = await requestPermission();
                  if (p === "granted" && pushSupported()) {
                    await enableWebPush();
                  }
                }}
                className="hv-btn hv-btn-ghost text-[10px] px-2 py-1"
                data-testid="notifications-enable-desktop"
              >
                Enable
              </button>
            </div>
          )}
          {permission === "granted" && pushSupported() && (
            <div className="px-3 py-2 border-t border-white/10 text-[10px] text-[#647087] flex items-center justify-between gap-2">
              <span>Closed-app push is on. </span>
              <button
                type="button"
                onClick={async () => {
                  await enableWebPush();
                }}
                className="text-[#1fb6c1] hover:underline"
                title="Re-subscribe this device"
                data-testid="notifications-resubscribe"
              >
                Resync
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
